import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable'
import { CircleHelp, Filter, Trash2 } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import AssignmentEntityChip from './AssignmentEntityChip'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import Card from 'app/frontend/base/components/Card'

type PaletteEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
  label: ReactNode
  category:
    | 'collection'
    | 'group'
    | 'specific_character'
    | 'by_role'
    | 'by_class'
  specificCharacterFilter?: {
    role: 'tank' | 'heal' | 'melee' | 'ranged' | null
    raidVisible: boolean
    forAlts: boolean
    teamRankId: number | null
    teamRankName: string
  }
}

type DraftEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
}

type DraftEntityLocation = {
  groupIndex: number
  index: number
  item: DraftEntity
}

type Props = {
  spellId: number
  entitiesByGroup: RaidPlanAssignmentEntity[][]
  paletteEntities: PaletteEntity[]
  assignmentUsageCountByIdentity: Record<string, number>
  canEdit: boolean
  onSave: (entitiesByGroup: RaidPlanAssignmentEntity[][]) => void
  onOpenChange: (open: boolean) => void
  trigger: ReactNode
}

type SpecificCharacterFilterRole = 'tank' | 'heal' | 'melee' | 'ranged'
type SpecificCharacterFilters = {
  roles: Record<SpecificCharacterFilterRole, boolean>
  enabledTeamRankIds?: number[]
}

const createDefaultSpecificCharacterFilters = (): SpecificCharacterFilters => ({
  roles: {
    tank: true,
    heal: true,
    melee: true,
    ranged: true,
  },
})

const parseSpecificCharacterFilters = (
  raw: string | null,
): SpecificCharacterFilters => {
  if (!raw) return createDefaultSpecificCharacterFilters()
  try {
    const parsed = JSON.parse(raw) as Partial<SpecificCharacterFilters> & {
      includeHiddenTeamRanks?: boolean
    }
    const hasValidStoredRankIds =
      Array.isArray(parsed.enabledTeamRankIds) &&
      parsed.enabledTeamRankIds.every((value) => typeof value === 'number')
    let enabledTeamRankIds: number[] | undefined
    if (hasValidStoredRankIds) {
      enabledTeamRankIds = parsed.enabledTeamRankIds
    }
    return {
      roles: {
        tank: parsed.roles?.tank ?? true,
        heal: parsed.roles?.heal ?? true,
        melee: parsed.roles?.melee ?? true,
        ranged: parsed.roles?.ranged ?? true,
      },
      enabledTeamRankIds,
    }
  } catch {
    return createDefaultSpecificCharacterFilters()
  }
}

const GroupDropTarget = ({
  id,
  className,
  onClick,
  children,
}: {
  id: string
  className?: string
  onClick?: () => void
  children: ReactNode
}) => {
  const { isOver, setNodeRef } = useDroppable({ id })
  return (
    <Card
      ref={setNodeRef}
      className={cn(className, isOver && 'border-primary')}
      onClick={onClick}
    >
      {children}
    </Card>
  )
}

const getSortableId = (id: string) => `draft-entity-${id}`

const parseSortableId = (id: string) => {
  const match = id.match(/^draft-entity-(.+)$/)
  if (!match) return null
  return match[1]
}

const getGroupDropTargetIndex = (id: string) => {
  const match = id.match(/^group-drop-(\d+)$/)
  if (!match) return null
  const groupIndex = Number(match[1])
  if (Number.isNaN(groupIndex)) return null
  return groupIndex
}

const findDraftEntityLocation = (
  groups: DraftEntity[][],
  draftEntityId: string,
): DraftEntityLocation | null => {
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const index = groups[groupIndex].findIndex(
      (item) => item.id === draftEntityId,
    )
    if (index >= 0) {
      return {
        groupIndex,
        index,
        item: groups[groupIndex][index],
      }
    }
  }
  return null
}

const PaletteEntity = ({
  label,
  assignmentCount,
  disabled,
  category,
  highlighted,
  onClick,
}: {
  label: ReactNode
  assignmentCount: number
  disabled: boolean
  category:
    | 'collection'
    | 'group'
    | 'specific_character'
    | 'by_role'
    | 'by_class'
  highlighted: boolean
  onClick: () => void
}) => {
  return (
    <Button
      type='button'
      variant='outline'
      size='sm'
      className={cn(
        'h-auto items-stretch justify-between gap-0 overflow-hidden p-0',
        disabled && 'cursor-not-allowed',
        highlighted && 'border-primary',
      )}
      onClick={onClick}
      disabled={disabled}
    >
      <span className='inline-flex min-w-0 flex-1 items-center px-2 py-1 text-left'>
        {label}
      </span>
      <span
        className={cn(
          'inline-flex shrink-0 items-center self-stretch border-l bg-muted px-2 text-sm text-muted-foreground tabular-nums',
          assignmentCount > 1 &&
            category !== 'by_role' &&
            category !== 'by_class' &&
            'text-warning-foreground bg-warning',
        )}
      >
        {assignmentCount}
      </span>
    </Button>
  )
}

const DraggableDraftEntity = ({
  sortableId,
  label,
  index,
  canEdit,
  onRemove,
}: {
  sortableId: string
  label: ReactNode
  index: number
  canEdit: boolean
  onRemove: () => void
}) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: !canEdit,
  })
  return (
    <div
      ref={setNodeRef}
      className='inline-flex items-center gap-1'
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        opacity: isDragging ? 0 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
    >
      <div
        ref={setActivatorNodeRef}
        className={cn(
          'min-w-0',
          canEdit && 'cursor-grab active:cursor-grabbing',
        )}
        {...(canEdit ? attributes : {})}
        {...(canEdit ? listeners : {})}
      >
        <AssignmentEntityChip
          index={index}
          label={label}
          endSlot={
            canEdit ? (
              <Trash2
                className='h-5 w-5 cursor-pointer px-1 text-muted-foreground hover:text-destructive-foreground transition-colors'
                onClick={onRemove}
              />
            ) : null
          }
        />
      </div>
    </div>
  )
}

const AssignmentBoxConfigurator = ({
  spellId,
  entitiesByGroup,
  paletteEntities,
  assignmentUsageCountByIdentity,
  canEdit,
  onSave,
  onOpenChange,
  trigger,
}: Props) => {
  const { t } = useTranslation()
  const filterStorageKey = `team-plan-assignment-source-filters:${spellId}`
  const [specificCharacterFilters, setSpecificCharacterFilters] =
    useState<SpecificCharacterFilters>(() => {
      if (typeof window === 'undefined') {
        return createDefaultSpecificCharacterFilters()
      }
      return parseSpecificCharacterFilters(
        localStorage.getItem(filterStorageKey),
      )
    })
  const [open, setOpen] = useState(false)
  const [activeGroupIndex, setActiveGroupIndex] = useState(0)
  const [activeDragEntityId, setActiveDragEntityId] = useState<string | null>(
    null,
  )
  const draftEntityIdCounter = useRef(0)
  const createDraftEntity = (
    entity: RaidPlanAssignmentEntity,
  ): DraftEntity => ({
    id: String(draftEntityIdCounter.current++),
    entity,
  })
  const buildDraftEntitiesByGroup = (
    sourceGroups: RaidPlanAssignmentEntity[][],
  ): DraftEntity[][] =>
    sourceGroups.map((group) => group.map(createDraftEntity))
  const [draftEntitiesByGroup, setDraftEntitiesByGroup] = useState<
    DraftEntity[][]
  >(buildDraftEntitiesByGroup(entitiesByGroup))
  const paletteLabelByIdentity = new Map(
    paletteEntities.map((entry) => [
      getAssignmentEntityIdentity(entry.entity),
      entry.label,
    ]),
  )
  const paletteEntitiesByCategory = useMemo(() => {
    const categories: Record<PaletteEntity['category'], PaletteEntity[]> = {
      collection: [],
      group: [],
      specific_character: [],
      by_role: [],
      by_class: [],
    }
    paletteEntities.forEach((entry) => {
      categories[entry.category].push(entry)
    })
    return categories
  }, [paletteEntities])
  const specificCharacterEntries =
    paletteEntitiesByCategory.specific_character ?? []
  const availableTeamRanks = useMemo(() => {
    const ranksById = new Map<
      number,
      { id: number; name: string; defaultVisible: boolean }
    >()
    specificCharacterEntries.forEach((entry) => {
      const rankId = entry.specificCharacterFilter?.teamRankId
      if (rankId === null || rankId === undefined) return
      const existing = ranksById.get(rankId)
      const isDefaultVisible =
        Boolean(entry.specificCharacterFilter?.raidVisible) &&
        !entry.specificCharacterFilter?.forAlts
      if (!existing) {
        ranksById.set(rankId, {
          id: rankId,
          name: entry.specificCharacterFilter?.teamRankName ?? 'Unknown',
          defaultVisible: isDefaultVisible,
        })
        return
      }
      if (isDefaultVisible) {
        existing.defaultVisible = true
      }
    })
    return [...ranksById.values()].sort((left, right) =>
      left.name.localeCompare(right.name),
    )
  }, [specificCharacterEntries])
  const defaultEnabledTeamRankIds = useMemo(
    () =>
      availableTeamRanks
        .filter((rank) => rank.defaultVisible)
        .map((rank) => rank.id),
    [availableTeamRanks],
  )
  const enabledTeamRankIds = useMemo(
    () =>
      specificCharacterFilters.enabledTeamRankIds ?? defaultEnabledTeamRankIds,
    [defaultEnabledTeamRankIds, specificCharacterFilters.enabledTeamRankIds],
  )
  const enabledTeamRankIdSet = useMemo(
    () => new Set(enabledTeamRankIds),
    [enabledTeamRankIds],
  )
  const hasSpecificCharacterFilterOverrides =
    Object.values(specificCharacterFilters.roles).some((enabled) => !enabled) ||
    enabledTeamRankIds.length !== defaultEnabledTeamRankIds.length ||
    enabledTeamRankIds.some(
      (rankId) => !defaultEnabledTeamRankIds.includes(rankId),
    )
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    setSpecificCharacterFilters(
      parseSpecificCharacterFilters(localStorage.getItem(filterStorageKey)),
    )
  }, [filterStorageKey])

  useEffect(() => {
    if (typeof window === 'undefined') return
    localStorage.setItem(
      filterStorageKey,
      JSON.stringify(specificCharacterFilters),
    )
  }, [filterStorageKey, specificCharacterFilters])

  const handleDragStart = (event: DragStartEvent) => {
    if (!canEdit) return
    const draftEntityId = parseSortableId(String(event.active.id))
    if (!draftEntityId) return
    setActiveDragEntityId(draftEntityId)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragEntityId(null)
    if (!canEdit) return
    const activeDraftEntityId = parseSortableId(String(event.active.id))
    if (!activeDraftEntityId) return
    const overId = event.over?.id ? String(event.over.id) : ''
    const overDraftEntityId = parseSortableId(overId)
    const groupDropTargetIndex = getGroupDropTargetIndex(overId)
    if (!overDraftEntityId && groupDropTargetIndex === null) return
    setDraftEntitiesByGroup((prev) => {
      const activeLocation = findDraftEntityLocation(prev, activeDraftEntityId)
      if (!activeLocation) return prev
      const next = prev.map((group) => [...group])
      if (overDraftEntityId) {
        const overLocation = findDraftEntityLocation(prev, overDraftEntityId)
        if (!overLocation) return prev
        if (activeLocation.groupIndex === overLocation.groupIndex) {
          if (activeLocation.index === overLocation.index) return prev
          next[activeLocation.groupIndex] = arrayMove(
            next[activeLocation.groupIndex],
            activeLocation.index,
            overLocation.index,
          )
          return next
        }
        const [moved] = next[activeLocation.groupIndex].splice(
          activeLocation.index,
          1,
        )
        if (!moved) return prev
        next[overLocation.groupIndex].push(moved)
        setActiveGroupIndex(overLocation.groupIndex)
        return next
      }
      if (groupDropTargetIndex === null) return prev
      if (groupDropTargetIndex === activeLocation.groupIndex) return prev
      const [moved] = next[activeLocation.groupIndex].splice(
        activeLocation.index,
        1,
      )
      if (!moved) return prev
      next[groupDropTargetIndex].push(moved)
      setActiveGroupIndex(groupDropTargetIndex)
      return next
    })
  }

  if (!canEdit) return <>{trigger}</>
  const normalizedGroups =
    draftEntitiesByGroup.length > 0 ? draftEntitiesByGroup : [[]]
  const safeActiveGroupIndex = Math.min(
    Math.max(activeGroupIndex, 0),
    normalizedGroups.length - 1,
  )
  const existingEntityKeys = new Set(
    normalizedGroups
      .flat()
      .map((item) => getAssignmentEntityIdentity(item.entity)),
  )
  const savedEntityCountByIdentity = entitiesByGroup
    .flat()
    .reduce<Record<string, number>>((acc, entity) => {
      const identity = getAssignmentEntityIdentity(entity)
      acc[identity] = (acc[identity] ?? 0) + 1
      return acc
    }, {})
  const draftEntityCountByIdentity = normalizedGroups
    .flat()
    .reduce<Record<string, number>>((acc, item) => {
      const identity = getAssignmentEntityIdentity(item.entity)
      acc[identity] = (acc[identity] ?? 0) + 1
      return acc
    }, {})
  const activeDragEntityLocation = activeDragEntityId
    ? findDraftEntityLocation(normalizedGroups, activeDragEntityId)
    : null
  const activeDragEntity = activeDragEntityLocation?.item

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setDraftEntitiesByGroup(buildDraftEntitiesByGroup(entitiesByGroup))
          setActiveGroupIndex(0)
          if (typeof window !== 'undefined') {
            setSpecificCharacterFilters(
              parseSpecificCharacterFilters(
                localStorage.getItem(filterStorageKey),
              ),
            )
          }
        }
        onOpenChange(nextOpen)
        setOpen(nextOpen)
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className='max-w-[70rem] gap-1'
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogTitle>
          {t('dashboard.team.plans.assignments.add-sources')}
        </DialogTitle>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDragEntityId(null)}
        >
          <div className='space-y-3'>
            {(
              [
                [
                  'collection',
                  'dashboard.team.plans.assignments.categories.collection',
                ],
                ['group', 'dashboard.team.plans.assignments.categories.group'],
                [
                  'specific_character',
                  'dashboard.team.plans.assignments.categories.specific-character',
                ],
                [
                  'by_role',
                  'dashboard.team.plans.assignments.categories.by-role',
                ],
                [
                  'by_class',
                  'dashboard.team.plans.assignments.categories.by-class',
                ],
              ] as const
            ).map(([category, titleKey]) => {
              const sortedCategoryEntities = (
                paletteEntitiesByCategory[category] ?? []
              ).filter((entry) => {
                if (category !== 'specific_character') return true
                const role = entry.specificCharacterFilter?.role
                const isRoleVisible = role
                  ? specificCharacterFilters.roles[role]
                  : true
                if (!isRoleVisible) return false
                const rankId = entry.specificCharacterFilter?.teamRankId
                if (rankId === null || rankId === undefined) return false
                return enabledTeamRankIdSet.has(rankId)
              })
              if (
                category !== 'specific_character' &&
                !sortedCategoryEntities.length
              ) {
                return null
              }
              return (
                <div key={category} className='space-y-1.5'>
                  <div className='text-xs text-muted-foreground inline-flex items-center gap-1'>
                    <span>{t(titleKey)}</span>
                    {category === 'specific_character' ? (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex'>
                              <CircleHelp className='h-3.5 w-3.5' />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t(
                              'dashboard.team.plans.assignments.categories.specific-character-tooltip',
                            )}
                          </TooltipContent>
                        </Tooltip>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type='button'
                              variant='ghost'
                              size='icon'
                              className={cn(
                                'h-4 w-4',
                                hasSpecificCharacterFilterOverrides &&
                                  'text-primary',
                              )}
                            >
                              <Filter className='h-3.5 w-3.5' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='start'>
                            <DropdownMenuLabel>
                              {t(
                                'dashboard.team.plans.assignments.categories.specific-character-filter-title',
                              )}
                            </DropdownMenuLabel>
                            {(
                              [
                                'tank',
                                'heal',
                                'melee',
                                'ranged',
                              ] as SpecificCharacterFilterRole[]
                            ).map((role) => (
                              <DropdownMenuCheckboxItem
                                key={role}
                                checked={specificCharacterFilters.roles[role]}
                                onCheckedChange={(checked) =>
                                  setSpecificCharacterFilters((prev) => ({
                                    ...prev,
                                    roles: {
                                      ...prev.roles,
                                      [role]: checked === true,
                                    },
                                  }))
                                }
                                onSelect={(event) => event.preventDefault()}
                              >
                                <span className='inline-flex items-center gap-1.5'>
                                  <CharacterRoleIcon
                                    role={role}
                                    size='small'
                                    className='shrink-0'
                                  />
                                  <span>
                                    {t(
                                      `dashboard.team.plans.assignments.entities.roles.${role}`,
                                    )}
                                  </span>
                                </span>
                              </DropdownMenuCheckboxItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>
                              {t(
                                'dashboard.team.plans.assignments.categories.specific-character-filter-ranks',
                              )}
                            </DropdownMenuLabel>
                            {availableTeamRanks.map((rank) => (
                              <DropdownMenuCheckboxItem
                                key={rank.id}
                                checked={enabledTeamRankIdSet.has(rank.id)}
                                onCheckedChange={(checked) =>
                                  setSpecificCharacterFilters((prev) => {
                                    const next = new Set(
                                      prev.enabledTeamRankIds ??
                                        defaultEnabledTeamRankIds,
                                    )
                                    if (checked === true) {
                                      next.add(rank.id)
                                    } else {
                                      next.delete(rank.id)
                                    }
                                    return {
                                      ...prev,
                                      enabledTeamRankIds: [...next],
                                    }
                                  })
                                }
                                onSelect={(event) => event.preventDefault()}
                              >
                                {rank.name}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    ) : null}
                  </div>
                  <div className='flex flex-wrap gap-1.5'>
                    {sortedCategoryEntities.length === 0 &&
                    category === 'specific_character' ? (
                      <div className='text-xs text-muted-foreground'>
                        {t(
                          'dashboard.team.plans.assignments.categories.specific-character-empty',
                        )}
                      </div>
                    ) : (
                      sortedCategoryEntities.map((entry) => {
                        const entryIdentity = getAssignmentEntityIdentity(
                          entry.entity,
                        )
                        const assignmentCount = Math.max(
                          0,
                          (assignmentUsageCountByIdentity[entryIdentity] ?? 0) -
                            (savedEntityCountByIdentity[entryIdentity] ?? 0) +
                            (draftEntityCountByIdentity[entryIdentity] ?? 0),
                        )
                        const isAlreadyAdded =
                          entry.category !== 'by_role' &&
                          entry.category !== 'by_class' &&
                          existingEntityKeys.has(entryIdentity)
                        return (
                          <PaletteEntity
                            key={entry.id}
                            label={entry.label}
                            assignmentCount={assignmentCount}
                            category={entry.category}
                            disabled={!canEdit}
                            highlighted={isAlreadyAdded}
                            onClick={() => {
                              setDraftEntitiesByGroup((prev) => {
                                const next = prev.map((group) => [...group])
                                const activeEntities =
                                  next[safeActiveGroupIndex] ?? []
                                if (isAlreadyAdded) {
                                  for (
                                    let groupIndex = 0;
                                    groupIndex < next.length;
                                    groupIndex += 1
                                  ) {
                                    next[groupIndex] = next[groupIndex].filter(
                                      (item) =>
                                        getAssignmentEntityIdentity(
                                          item.entity,
                                        ) !== entryIdentity,
                                    )
                                  }
                                  return next
                                }
                                next[safeActiveGroupIndex] = [
                                  ...activeEntities,
                                  createDraftEntity(entry.entity),
                                ]
                                return next
                              })
                            }}
                          />
                        )
                      })
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div
            className={cn('grid gap-2 mt-2 pt-2 border-t border-border')}
            style={{
              gridTemplateColumns: `repeat(${Math.max(
                Math.min(normalizedGroups.length, 4),
                1,
              )}, minmax(0, 1fr))`,
            }}
          >
            {normalizedGroups.map((groupEntities, groupIndex) => {
              const isActive = groupIndex === safeActiveGroupIndex
              return (
                <div key={`group-${groupIndex}`}>
                  <div className='mb-1 flex items-center justify-between gap-2'>
                    <span className='text-xs text-muted-foreground'>
                      {normalizedGroups.length > 1
                        ? t('dashboard.team.plans.assignments.group-label', {
                            count: groupIndex + 1,
                          })
                        : 'Selected entities'}
                    </span>
                    <Button
                      type='button'
                      variant='outline'
                      size='xs'
                      onClick={() =>
                        setDraftEntitiesByGroup((prev) => {
                          const next = prev.map((group) => [...group])
                          next[groupIndex] = []
                          return next
                        })
                      }
                      disabled={groupEntities.length === 0}
                    >
                      {t('dashboard.team.plans.assignments.clear-all')}
                    </Button>
                  </div>
                  <GroupDropTarget
                    id={`group-drop-${groupIndex}`}
                    className={cn(
                      'border-dashed p-2 min-h-12',
                      normalizedGroups.length > 1
                        ? isActive
                          ? 'border-primary'
                          : 'cursor-pointer hover:border-primary/50'
                        : '',
                    )}
                    onClick={() => setActiveGroupIndex(groupIndex)}
                  >
                    {groupEntities.length === 0 ? (
                      <div className='text-xs text-muted-foreground'>
                        {isActive
                          ? t('dashboard.team.plans.assignments.drop-hint')
                          : t(
                              'dashboard.team.plans.assignments.inactive-drop-hint',
                            )}
                      </div>
                    ) : (
                      <SortableContext
                        items={groupEntities.map((item) =>
                          getSortableId(item.id),
                        )}
                        strategy={rectSortingStrategy}
                      >
                        <div className='flex flex-wrap items-center gap-1'>
                          {groupEntities.map((item, entityIndex) => (
                            <div
                              key={item.id}
                              className='inline-flex items-center'
                            >
                              <DraggableDraftEntity
                                sortableId={getSortableId(item.id)}
                                label={
                                  paletteLabelByIdentity.get(
                                    getAssignmentEntityIdentity(item.entity),
                                  ) ?? getAssignmentEntityLabel(t, item.entity)
                                }
                                index={entityIndex}
                                canEdit={canEdit}
                                onRemove={() =>
                                  setDraftEntitiesByGroup((prev) => {
                                    const next = prev.map((group) => [...group])
                                    next[groupIndex] = next[groupIndex].filter(
                                      (groupItem) => groupItem.id !== item.id,
                                    )
                                    return next
                                  })
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </SortableContext>
                    )}
                  </GroupDropTarget>
                </div>
              )
            })}
          </div>
          {typeof document !== 'undefined'
            ? createPortal(
                <DragOverlay>
                  {activeDragEntity ? (
                    <AssignmentEntityChip
                      index={activeDragEntityLocation?.index ?? 0}
                      label={
                        paletteLabelByIdentity.get(
                          getAssignmentEntityIdentity(activeDragEntity.entity),
                        ) ??
                        getAssignmentEntityLabel(t, activeDragEntity.entity)
                      }
                    />
                  ) : null}
                </DragOverlay>,
                document.body,
              )
            : null}
        </DndContext>

        <div className='flex items-center justify-end gap-2 mt-3'>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              setDraftEntitiesByGroup(
                buildDraftEntitiesByGroup(entitiesByGroup),
              )
              setActiveGroupIndex(0)
              onOpenChange(false)
              setOpen(false)
            }}
          >
            {t('shared.cancel')}
          </Button>
          <Button
            type='button'
            onClick={() => {
              onSave(
                normalizedGroups.map((group) =>
                  group.map((item) => item.entity),
                ),
              )
              onOpenChange(false)
              setOpen(false)
            }}
          >
            {t('shared.save-changes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssignmentBoxConfigurator
